/**
 * IBAN validation for the frontend.
 *
 * Mirror of `normaliseIban` / `isValidIban` in
 * `supabase/functions/_shared/sepa.ts`, asserted identical by
 * `src/lib/iban.parity.test.ts`. The edge-function copy cannot be imported
 * directly by the app bundle, and a third hand-written copy is how the two
 * that already existed drifted apart in the first place — `SepaMandateForm`
 * carried a private duplicate of exactly this function.
 *
 * Keep both copies in step or neither: an IBAN the form accepts and the server
 * rejects strands an owner mid-order, and one the form rejects and the server
 * would have accepted is a payout address they cannot enter.
 */

/** Strip spaces and upper-case, the form IBANs are compared in. */
export function normaliseIban(iban: string): string {
  return iban.replace(/\s+/g, "").toUpperCase();
}

/** SEPA IBAN mod-97 validation. Rejects malformed/unknown IBANs early. */
export function isValidIban(raw: string): boolean {
  const iban = normaliseIban(raw);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(iban)) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const value = /\d/.test(ch) ? ch : (ch.charCodeAt(0) - 55).toString();
    for (const digit of value) {
      remainder = (remainder * 10 + Number(digit)) % 97;
    }
  }
  return remainder === 1;
}
